<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div
    v-if="resolvedCategories.length > 0 || $slots['prepend-tabs']"
    class="predefined-views-panel"
  >
    <PrimeSelect
      v-if="resolvedCategories.length > 1"
      v-model="selectedCategory"
      :options="resolvedCategories"
      option-label="label"
      option-value="id"
      size="small"
    />
    <span
      v-if="resolvedCategories.length > 1 && currentViews.length > 0"
      class="panel-separator"
    />
    <div
      v-if="currentViews.length > 0 || $slots['prepend-tabs']"
      class="tabs-wrapper"
    >
      <PrimeTabs v-model:value="selectedViewId" scrollable>
        <PrimeTabList>
          <slot name="prepend-tabs" />
          <PrimeTab
            v-for="view in currentViews"
            :key="view.id"
            :value="view.id"
          >
            {{ view.name }}
          </PrimeTab>
        </PrimeTabList>
      </PrimeTabs>
    </div>
  </div>
</template>

<script>
import Select from "primevue/select";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";

export default {
  name: "PredefinedViewsPanel",
  components: {
    PrimeSelect: Select,
    PrimeTabs: Tabs,
    PrimeTabList: TabList,
    PrimeTab: Tab,
  },
  props: {
    ancestorNodeId: {
      type: String,
      required: true,
    },
    displayContext: {
      type: String,
      default: "folderView",
    },
    initialCategory: {
      type: String,
      default: null,
    },
    initialViewId: {
      type: String,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    additionalViews: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["load-view", "category-change"],
  data() {
    return {
      selectedCategory: null,
      selectedViewId: null,
      isRestoringState: false,
    };
  },
  computed: {
    allViews() {
      return this.additionalViews;
    },
    resolvedCategories() {
      return this.categories.filter(
        (cat) =>
          cat.display === undefined ||
          cat.display.includes(this.displayContext),
      );
    },
    selectedCategoryObject() {
      return this.resolvedCategories.find(
        (c) => c.id === this.selectedCategory,
      );
    },
    currentViews() {
      if (!this.selectedCategory) {
        return [];
      }
      const categoryViewFiles = this.selectedCategoryObject?.views || [];
      return this.allViews.filter(
        (v) => v.filename && categoryViewFiles.includes(v.filename),
      );
    },
  },
  watch: {
    resolvedCategories: {
      handler(categories) {
        // Reset selection when current category is no longer in the filtered list
        if (
          this.selectedCategory &&
          !categories.some((c) => c.id === this.selectedCategory)
        ) {
          this.selectedCategory = null;
          this.selectedViewId = null;
        }
        if (categories.length > 0 && !this.selectedCategory) {
          if (
            this.initialCategory &&
            categories.some((c) => c.id === this.initialCategory)
          ) {
            this.isRestoringState = true;
            this.selectedCategory = this.initialCategory;
          } else {
            this.selectedCategory = categories[0].id;
          }

          // Restore initial view if it belongs to this category
          // (currentViews is now computed based on selectedCategory)
          this.$nextTick(() => {
            const views = this.currentViews;
            if (
              this.initialViewId &&
              views.some((v) => v.id === this.initialViewId)
            ) {
              this.selectedViewId = this.initialViewId;
              const view = views.find((v) => v.id === this.initialViewId);
              this.$emit("load-view", {
                view,
                ancestorNodeId: this.ancestorNodeId,
              });
            } else {
              this.isRestoringState = false;
              this.$emit("category-change", {
                categoryId: this.selectedCategory,
                hasViews: views.length > 0,
              });
            }
          });
        }
      },
      immediate: true,
    },
    selectedCategory(categoryId) {
      if (this.isRestoringState) {
        this.isRestoringState = false;
        return;
      }
      this.selectedViewId = null;
      this.$nextTick(() => {
        this.$emit("category-change", {
          categoryId,
          hasViews: this.currentViews.length > 0,
        });
      });
    },
    selectedViewId(newId) {
      if (newId == null) {
        return;
      }
      const view = this.allViews.find((v) => v.id === newId);
      if (view) {
        this.$emit("load-view", {
          view,
          ancestorNodeId: this.ancestorNodeId,
        });
      }
    },
    initialViewId(newId) {
      if (!newId) {
        return;
      }
      const view = this.allViews.find((v) => v.id === newId);
      if (view && this.currentViews.some((v) => v.id === newId)) {
        this.selectedViewId = newId;
      }
    },
  },
};
</script>

<style scoped>
.predefined-views-panel {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  overflow: hidden;
}

.tabs-wrapper {
  overflow: hidden;
  min-width: 0;
  flex: 1;
}

.panel-separator {
  width: 1px;
  height: 1.5rem;
  background: var(--p-surface-border);
  flex-shrink: 0;
}
</style>
